// src/hooks/use-pto-request-list.ts
import { useEffect, useRef, useState } from "react";
import {
  type DecisionAction,
  type DecisionState,
  decisionFailed,
  decisionSucceeded,
  isMayHaveSaved,
  NO_DECISIONS,
  pruneDecisions,
  startDecision,
} from "@/lib/team/pto-decisions";
import type { PtoRequestView } from "@/lib/team/pto-list";

export interface PtoRequestListState {
  decisions: DecisionState;
  /** Runs one row's write, holding the row; `mutateAsync` settles each call, not the latest. */
  decide: (
    row: PtoRequestView,
    action: DecisionAction,
    call: () => Promise<unknown>,
  ) => Promise<{ ok: boolean; maySaved: boolean }>;
  showHolidays: boolean;
  setShowHolidays: (show: boolean) => void;
}

/**
 * The approvals list's row locks and holiday switch, owned by the screen rather than the list, so
 * a row held after a 504 stays held when My Team's dialog is closed and opened again. Each list
 * read (`rows`, read at `updatedAt`) drops the locks it shows settled, so a lock never outlives
 * the request state it was made for.
 */
export function usePtoRequestList(
  rows: readonly PtoRequestView[] | undefined,
  updatedAt: number,
): PtoRequestListState {
  const [decisions, setDecisions] = useState(NO_DECISIONS);
  const [showHolidays, setShowHolidays] = useState(false);
  // The latest read, so a call that settles after its read landed is pruned against it too.
  const latest = useRef<{ rows: readonly PtoRequestView[] | undefined; updatedAt: number }>({
    rows,
    updatedAt,
  });
  useEffect(() => {
    latest.current = { rows, updatedAt };
    if (rows) setDecisions((s) => pruneDecisions(s, rows, updatedAt));
  }, [rows, updatedAt]);
  const settle = (s: DecisionState): DecisionState => {
    const l = latest.current;
    return l.rows ? pruneDecisions(s, l.rows, l.updatedAt) : s;
  };
  const decide: PtoRequestListState["decide"] = async (row, action, call) => {
    setDecisions((s) => startDecision(s, row, action));
    try {
      await call();
      setDecisions((s) => settle(decisionSucceeded(s, row.id)));
      return { ok: true, maySaved: false };
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setDecisions((s) => decisionFailed(s, row.id, e, Date.now()));
      return { ok: false, maySaved: isMayHaveSaved(e) };
    }
  };
  return { decisions, decide, showHolidays, setShowHolidays };
}
